
export interface ItemBonusView {
  /** An `EApplyTypes` index (`common/length.h:332-433`) -- the item side of the
   *  original's two parallel numberings, exactly as `itemattr.Bonus.apply`
   *  stores it and `ItemBonus` sends it. `ui/itembonus.ts` turns it into words. */
  readonly apply: number;
  /** Signed, so a penalty (`itemattr` never rolls one today, but `AffectSlot`
   *  already carries signed values for the same reason) reads red rather than
   *  green. */
  readonly value: number;
}

/** The static columns of a proto row -- true of every copy of the item, and so
 *  the most a dealer's shelf can say. */
export interface ItemStats {
  /** The level below which this cannot be worn. Zero means no gate. */
  readonly levelLimit: number;
  /** A weapon's damage floor and ceiling, `VALUE5 * 2` already added to both.
   *  Zero on anything that is not a weapon. */
  readonly attackMin: number;
  readonly attackMax: number;
  /** Armour's combined defence, already the total `ItemDef.defence_bonus`
   *  computes server-side -- never recomputed from smaller parts here. Zero for
   *  wrists, necklaces and earrings, which carry bonuses rather than armour
   *  (`char.cpp:2070`). */
  readonly defence: number;
  /** What a use restores, flat and as a percentage. */
  readonly healHp: number;
  readonly healHpPct: number;
  /** Which classes may *not* use this: bit0 Warrior, bit1 Ninja, bit2 Sura,
   *  bit3 Shaman. A refusal mask, mirroring the proto's own `ANTI_*` flags
   *  directly rather than inverting it into an allow-list the server would
   *  have to build a second way. */
  readonly barredJobs: number;
}

/** A particular item: the proto's columns plus what this copy rolled and what
 *  is fitted in it. Both are per-instance and neither can be derived from the
 *  vnum. */
export interface ItemFacts extends ItemStats {
  /** What this instance rolled when it dropped, as `(apply, value)` pairs.
   *  Empty for the overwhelming majority of items. `ui/itembonus.ts` turns a
   *  pair into a sentence -- the server deliberately does not. */
  readonly bonuses: readonly ItemBonusView[];
  /** Server-reported first socket: 0 empty, 1 broken, otherwise a fitted Metin
   *  whose applies are read from the equipped item (`item.cpp:590-624`). */
  readonly socket: number;
  /** Server-reported second and third sockets; never accepted from a client
   *  action. Optional because a panel's own fixture may not bother with them. */
  readonly socket1?: number;
  readonly socket2?: number;
}

/** The same block in the wire's spelling, which two panels hold directly: the
 *  trade window and the storeroom hand the decoded frame straight on, because
 *  a re-shaped copy is a second declaration of the struct. */
export interface WireItemFacts {
  readonly level_limit: number;
  readonly attack_min: number;
  readonly attack_max: number;
  readonly defence: number;
  readonly heal_hp: number;
  readonly heal_hp_pct: number;
  readonly barred_jobs: number;
  readonly bonuses: readonly { readonly apply: number; readonly value: number }[];
  readonly socket: number;
  readonly socket1: number;
  readonly socket2: number;
}

/** The one rename from the wire's spelling to the client's. Every field, in one
 *  place, so a field added to the schema is added here once. */
export function itemFactsOf(wire: WireItemFacts): ItemFacts {
  return {
    levelLimit: wire.level_limit,
    attackMin: wire.attack_min,
    attackMax: wire.attack_max,
    defence: wire.defence,
    healHp: wire.heal_hp,
    healHpPct: wire.heal_hp_pct,
    barredJobs: wire.barred_jobs,
    // Copied out rather than passed through, so the panel holds plain data and
    // not a decoder's class instance -- the rule every other field follows.
    bonuses: wire.bonuses.map((bonus) => ({ apply: bonus.apply, value: bonus.value })),
    socket: wire.socket,
    socket1: wire.socket1,
    socket2: wire.socket2,
  };
}

export const NO_ITEM_FACTS: ItemFacts = {
  levelLimit: 0,
  attackMin: 0,
  attackMax: 0,
  defence: 0,
  healHp: 0,
  healHpPct: 0,
  barredJobs: 0,
  bonuses: [],
  socket: 0,
  socket1: 0,
  socket2: 0,
};

/** One item written the way a chat line carries it: `|cCOLOUR|Hitem:...|h[Name]|h|r`.
 *
 * `playerGetItemLink` prints the vnum, the flags and the three sockets as
 * lowercase hex, then one `:type:value` pair per rolled attribute in decimal,
 * and picks the gold colour when there was at least one such pair and the pale
 * one when there was not (`PythonPlayerModule.cpp:1010-1069`).
 */
export function itemLink(
  vnum: number,
  name: string,
  facts: {
    readonly flags?: number;
    readonly socket?: number;
    readonly socket1?: number;
    readonly socket2?: number;
    readonly bonuses?: readonly ItemBonusView[];
  } = {},
): string {
  const hex = (value: number): string => (value >>> 0).toString(16);
  let body =
    `item:${hex(vnum)}:${hex(facts.flags ?? 0)}:${hex(facts.socket ?? 0)}` +
    `:${hex(facts.socket1 ?? 0)}:${hex(facts.socket2 ?? 0)}`;
  let rolled = false;
  for (const bonus of facts.bonuses ?? []) {
    if (bonus.apply === 0) continue;
    body += `:${hex(bonus.apply)}:${bonus.value}`;
    rolled = true;
  }
  // `0xffffc700` for an item that rolled something, `0xfff1e6c0` for one that
  // did not (`PythonPlayerModule.cpp:1060-1063`).
  return `|c${rolled ? "ffffc700" : "fff1e6c0"}|H${body}|h[${name}]|h|r`;
}
